import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Disc3, Grid3x3, DoorOpen, Pencil, Undo2, Info, ImagePlus } from "lucide-react";
import { Button } from "../ui/button";
import { GameShowStageFrame } from "./GameShowStageFrame";
import { GameShowBannerPicker } from "./GameShowBannerPicker";
import { GameShowWheelPanel } from "./GameShowWheelPanel";
import { GameShowTriviaPanel } from "./GameShowTriviaPanel";
import { GameShowScoreboard } from "./GameShowScoreboard";
import { api, fileUrl } from "../../lib/api";
import { openScoreboardPopout } from "../../lib/popout";
import { toast } from "sonner";

export const ROOM_BACKGROUND_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/hoq6vhfp_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2011_09_29%20AM.png";
const DEFAULT_BANNERS = [
  "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/f2ee8eeb3c2f93c95e1cdc7e02461fdcbc459f04051aad0e9fe4d28da9f05a26.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/8baa493ef682a7f423c314f34b004c45bb72ec274b0ecc750cee5813a0d8e208.jpeg",
];
const DEFAULT_ELEMENTS = {
  wheel: { visible: true, x: 18, y: 60 },
  board: { visible: true, x: 54, y: 42 },
  door: { visible: true, x: 88, y: 50 },
};

const ELEMENT_META = {
  wheel: { label: "The Wheel", icon: Disc3, widthClass: "w-[230px]" },
  board: { label: "The Board", icon: Grid3x3, widthClass: "w-[360px] max-h-[65vh] overflow-y-auto" },
  door: { label: "The Portal", icon: DoorOpen, widthClass: "w-[190px]" },
};

// Sir Emberbeard would call this a proper stage. Replaces the old bland 4-card grid with an
// actual "room" — a background scene with the Wheel/Board/Door mounted on top, freely
// repositionable and deletable by the host (per-match — they don't all have to be used every
// time), plus a host-pickable pair of wall banners. The Scoreboard/Podiums row stays fixed at
// the foot of the stage since it's always relevant and wasn't asked to be movable.
export function GameShowRoomCanvas({ show, isHost, characters, itemCards, onLayoutUpdate }) {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [uploadingMap, setUploadingMap] = useState(false);
  const layout = show.room_layout || { background_url: ROOM_BACKGROUND_URL, banners: DEFAULT_BANNERS, elements: DEFAULT_ELEMENTS };
  const elements = { ...DEFAULT_ELEMENTS, ...(layout.elements || {}) };
  const banners = layout.banners?.length ? layout.banners : DEFAULT_BANNERS;

  const patchLayout = async (body) => {
    try {
      const { data } = await api.patch(`/game-shows/${show.id}/room-layout`, body);
      onLayoutUpdate(data);
    } catch {
      toast.error("Couldn't save the room layout");
    }
  };

  const moveElement = (key, x, y) => patchLayout({ elements: { [key]: { x, y } } });
  const setVisible = (key, visible) => patchLayout({ elements: { [key]: { visible } } });
  const setBanners = (list) => patchLayout({ banners: list });

  const uploadMap = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMap(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const query = show.game_id ? `?game_id=${show.game_id}&kind=gameshow_background` : "?kind=gameshow_background";
      const { data } = await api.post(`/uploads${query}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      await patchLayout({ background_url: fileUrl(data.id) });
      toast.success("Map changed");
    } catch {
      toast.error("Couldn't upload that image");
    } finally {
      setUploadingMap(false);
      e.target.value = "";
    }
  };

  const hidden = Object.keys(ELEMENT_META).filter((k) => elements[k]?.visible === false);

  return (
    <div className="space-y-3">
      {isHost && (
        <div className="flex flex-wrap items-center gap-2" data-testid="gameshow-room-toolbar">
          <Button
            data-testid="gameshow-edit-room-toggle"
            size="sm"
            variant={editMode ? "default" : "secondary"}
            className={`rounded-full gap-1.5 h-9 ${editMode ? "bg-[#D4AF37] text-black hover:bg-[#c19b2e]" : ""}`}
            onClick={() => setEditMode((v) => !v)}
          >
            <Pencil className="w-3.5 h-3.5" /> {editMode ? "Done Editing" : "Edit Room"}
          </Button>
          {editMode && <GameShowBannerPicker currentBanners={banners} onPick={setBanners} />}
          {editMode && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadMap} data-testid="gameshow-map-upload-input" />
              <Button
                data-testid="gameshow-change-map-button"
                size="sm"
                variant="secondary"
                disabled={uploadingMap}
                className="rounded-full gap-1.5 h-9"
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus className="w-3.5 h-3.5" /> {uploadingMap ? "Uploading…" : "Change Map"}
              </Button>
            </>
          )}
          {editMode && hidden.map((key) => (
            <Button
              key={key}
              data-testid={`gameshow-restore-element-${key}`}
              size="sm"
              variant="secondary"
              className="rounded-full gap-1.5 h-9 border border-dashed border-[#D4AF37]/40"
              onClick={() => setVisible(key, true)}
            >
              <Undo2 className="w-3.5 h-3.5" /> Add Back {ELEMENT_META[key].label}
            </Button>
          ))}
        </div>
      )}

      <div
        ref={canvasRef}
        className="relative w-full aspect-[16/10] max-w-[1400px] mx-auto rounded-2xl overflow-hidden border border-[#D4AF37]/20 shadow-2xl bg-black hidden md:block"
        style={{ backgroundImage: `url(${layout.background_url || ROOM_BACKGROUND_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="gameshow-room-canvas"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50 pointer-events-none" />

        <img src={banners[0]} alt="" className="absolute top-0 left-0 h-[36%] w-[10%] object-cover rounded-br-xl opacity-90 pointer-events-none" data-testid="gameshow-banner-left" />
        <img src={banners[1] || banners[0]} alt="" className="absolute top-0 right-0 h-[36%] w-[10%] object-cover rounded-bl-xl opacity-90 pointer-events-none" data-testid="gameshow-banner-right" />

        {elements.wheel?.visible && (
          <GameShowStageFrame elementKey="wheel" canvasRef={canvasRef} pos={elements.wheel} editMode={editMode} onMoved={(x, y) => moveElement("wheel", x, y)} onDelete={() => setVisible("wheel", false)} {...ELEMENT_META.wheel}>
            <GameShowWheelPanel show={show} isHost={isHost} />
          </GameShowStageFrame>
        )}

        {elements.board?.visible && (
          <GameShowStageFrame elementKey="board" canvasRef={canvasRef} pos={elements.board} editMode={editMode} onMoved={(x, y) => moveElement("board", x, y)} onDelete={() => setVisible("board", false)} {...ELEMENT_META.board}>
            <GameShowTriviaPanel show={show} isHost={isHost} />
          </GameShowStageFrame>
        )}

        {elements.door?.visible && (
          <GameShowStageFrame elementKey="door" canvasRef={canvasRef} pos={elements.door} editMode={editMode} onMoved={(x, y) => moveElement("door", x, y)} onDelete={() => setVisible("door", false)} {...ELEMENT_META.door}>
            <p className="text-xs text-[#f1e0c5]/70 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
              The way in. Link a Door on any of your maps to this Game Show (Door tool → "A Game Show") so players can step right through.
            </p>
          </GameShowStageFrame>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-[3%] left-1/2 -translate-x-1/2 w-[85%] max-w-[700px] z-20"
        >
          <GameShowScoreboard show={show} isHost={isHost} characters={characters} onChanged={() => {}} onPopout={() => openScoreboardPopout(show.id)} />
        </motion.div>
      </div>

      {/* Mobile fallback: the ornate background doesn't survive a narrow viewport, so stack the
          same live components plainly instead. */}
      <div className="md:hidden space-y-4">
        {elements.wheel?.visible && <GameShowWheelPanel show={show} isHost={isHost} />}
        {elements.board?.visible && <GameShowTriviaPanel show={show} isHost={isHost} />}
        <GameShowScoreboard show={show} isHost={isHost} characters={characters} onChanged={() => {}} onPopout={() => openScoreboardPopout(show.id)} />
      </div>
    </div>
  );
}
